import React, { useState } from 'react';
import { StepProps, SPECIALTY_COLORS } from '../../types';
import { motion, AnimatePresence } from 'framer-motion';
import { Droplets, Heart, FlaskConical, Stethoscope, ArrowRight, Lightbulb, MessageCircle, ChevronDown, Link2 } from 'lucide-react';
interface ProfileData {
  icon: React.ElementType;
  beliefs: string[];
  priorities: string[];
  // Each priority maps to a belief index, showing the connection
  connections: number[];
  tension: string;
  predictionPrompt: string;
}
const PROFILES: Record<string, ProfileData> = {
  Nephrologist: {
    icon: Droplets,
    beliefs: ['Late-stage kidney specialist leading care in advanced/high-risk CKD', 'Often manage a more fragile patient population', 'Motivated by clinical trial data and new advances in CKD', "Frustrated by late referrals and end-stage 'fire-fighting'"],
    priorities: ['Slowing CKD progression while managing hyperkalaemia/safety', 'Cautious, gradual treatment influenced by fear of doing harm', 'Hesitant to assume broader management due to high workloads', 'Seek evidence-based direction on treatment sequencing'],
    connections: [1, 0, 3, 2],
    tension: 'Motivated by evidence but constrained by late referrals and safety concerns',
    predictionPrompt: 'Given these beliefs, how do you think nephrologists prioritise treatment? What would they focus on first — and what would they avoid?'
  },
  Cardiologist: {
    icon: Heart,
    beliefs: ['Focus on CV events and downstream HF/CKD diagnosed patients', "Pride themselves on adopting a proactive, 'aggressive' approach", 'Recognize interplay between heart and kidney health', "Welcome shift from needing to 'balance' treatments to dual-organ protection"],
    priorities: ['Require proven CV benefit in CKD', 'Comfortable with simultaneous or rapid-sequence combination', 'Strongly driven by outcome prevention', 'Less concerned by initial eGFR decline for better overall control'],
    connections: [0, 1, 2, 3],
    tension: 'Proactive and outcome-driven but may overlook kidney-specific markers',
    predictionPrompt: 'Given these beliefs, how aggressively do you think cardiologists treat CKD? What evidence would they demand before acting?'
  },
  Endocrinologist: {
    icon: FlaskConical,
    beliefs: ['Retain patient contact throughout diabetic care', 'Prioritize metabolic control and weight management as preventative', "Cautious to 'over-step' into reactive management of progressive decline", "Lead early CKD escalation but refer for 'non-glycaemic' treatment"],
    priorities: ['Prioritize treatments with metabolic effect, followed by kidney protection', 'Support multi-modal care as an escalation strategy', 'Data-driven specialty, collating biomarker information', 'Routine monitoring of UACR to identify early-stage CKD'],
    connections: [1, 3, 0, 2],
    tension: 'Preventative mindset but cautious about overstepping into non-metabolic management',
    predictionPrompt: 'Given these beliefs, where do you think endocrinologists draw the line? What would they treat themselves vs. refer out?'
  },
  PCP: {
    icon: Stethoscope,
    beliefs: ['Manage early-stage CKM before referring for specialist care', 'Broader role propelled by comfort with SGLT2 and GLP1', "Tend to stick with 'tried and true' therapies", 'Low confidence in escalating therapies'],
    priorities: ['Holistic treatment approach and focus on prevention', 'Adopt a conservative, step-wise approach to treatment', 'Strong desire for simplified treatment approaches', 'Looking for guidelines and social-proof / peer advocacy'],
    connections: [0, 2, 3, 1],
    tension: 'Broad patient access but low confidence in escalation',
    predictionPrompt: 'Given these beliefs, how do you think PCPs decide when to escalate treatment? What would give them confidence to act?'
  }
};
export function SpecialtyDeepDive({
  state,
  nextStep
}: StepProps) {
  const role = state.selectedSpecialty || 'Nephrologist';
  const profile = PROFILES[role];
  const Icon = profile.icon;
  const color = SPECIALTY_COLORS[role];
  const [phase, setPhase] = useState<'beliefs' | 'reveal' | 'insight'>('beliefs');
  const [revealedCount, setRevealedCount] = useState(0);
  const [highlightedBelief, setHighlightedBelief] = useState<number | null>(null);
  const handleReveal = () => {
    setPhase('reveal');
    // Stagger reveal priorities one by one
    let count = 0;
    const interval = setInterval(() => {
      count++;
      setRevealedCount(count);
      if (count >= profile.priorities.length) {
        clearInterval(interval);
        setTimeout(() => setPhase('insight'), 600);
      }
    }, 800);
  };
  return <div className="flex-grow flex flex-col py-12 px-6 overflow-y-auto pt-20" data-id="element-424">
      <div className="max-w-5xl w-full mx-auto" data-id="element-425">
        {/* Header */}
        <div className="flex items-center mb-10" data-id="element-426">
          <div className="p-4 rounded-2xl mr-6" style={{
          backgroundColor: `${color}15`,
          color
        }} data-id="element-427">
            <Icon size={40} data-id="element-428" />
          </div>
          <div data-id="element-429">
            <div className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-1" data-id="element-430">
              Group {state.groupName || 'Deep Dive'}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-dark" data-id="element-431">
              Inside the {role} Mindset
            </h2>
            <p className="text-gray-500 text-lg mt-1" data-id="element-432">
              {phase === 'beliefs' ? 'Start with what they believe — then predict what follows.' : phase === 'reveal' ? 'See how beliefs shape treatment priorities.' : 'The full picture — beliefs, priorities, and the tension between them.'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8" data-id="element-433">
          {/* BELIEFS — Always visible */}
          <div className="bg-gray-50 rounded-3xl p-8 border border-gray-100" data-id="element-434">
            <h3 className="text-xl font-bold mb-6 flex items-center" style={{
            color
          }} data-id="element-435">
              <span className="w-8 h-px bg-current mr-4 opacity-50" data-id="element-436"></span>
              What they believe
            </h3>
            <ul className="space-y-4" data-id="element-437">
              {profile.beliefs.map((belief, i) => {
              const isHighlighted = highlightedBelief === i;
              return <motion.li key={i} className={`flex items-start p-3 -mx-3 rounded-xl transition-colors duration-300 ${isHighlighted ? 'bg-white shadow-sm' : ''}`} animate={{
                scale: isHighlighted ? 1.02 : 1
              }} transition={{
                duration: 0.3
              }} data-id="element-438">
                    <span className="mr-3 mt-1.5 w-2 h-2 rounded-full flex-shrink-0 transition-all duration-300" style={{
                  backgroundColor: color,
                  transform: isHighlighted ? 'scale(1.5)' : 'scale(1)'
                }} data-id="element-439"></span>
                    <span className={`leading-relaxed transition-colors duration-300 ${isHighlighted ? 'text-dark font-medium' : 'text-gray-700'}`} data-id="element-440">
                      {belief}
                    </span>
                  </motion.li>;
            })}
            </ul>
          </div>

          {/* PRIORITIES — Hidden initially, revealed one by one */}
          <div className="relative" data-id="element-441">
            <AnimatePresence mode="wait" data-id="element-442">
              {phase === 'beliefs' ? <motion.div key="prediction" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} className="bg-white rounded-3xl p-8 border-2 border-dashed border-gray-300 h-full flex flex-col justify-center" data-id="element-443">
                  <div className="text-center" data-id="element-444">
                    <MessageCircle size={36} className="mx-auto mb-4 text-blue-500" data-id="element-445" />
                    <h3 className="text-xl font-bold text-dark mb-4" data-id="element-446">
                      Your group's prediction
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-6" data-id="element-447">
                      {profile.predictionPrompt}
                    </p>
                    <p className="text-sm text-gray-400 italic mb-8" data-id="element-448">
                      Take 2-3 minutes to discuss before revealing the answer.
                    </p>
                    <button onClick={handleReveal} className="inline-flex items-center justify-center px-6 py-3 text-base font-semibold text-white rounded-full transition-colors" style={{
                  backgroundColor: color
                }} data-id="element-449">
                      Reveal Treatment Priorities
                      <ChevronDown className="ml-2 w-5 h-5" data-id="element-450" />
                    </button>
                  </div>
                </motion.div> : <motion.div key="priorities" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} className="bg-gray-50 rounded-3xl p-8 border border-gray-100" data-id="element-451">
                  <h3 className="text-xl font-bold mb-6 flex items-center" style={{
                color
              }} data-id="element-452">
                    <span className="w-8 h-px bg-current mr-4 opacity-50" data-id="element-453"></span>
                    Treatment priorities
                  </h3>
                  <ul className="space-y-4" data-id="element-454">
                    {profile.priorities.map((priority, i) => {
                  const isRevealed = i < revealedCount;
                  const connectedBelief = profile.connections[i];
                  return <motion.li key={i} initial={{
                    opacity: 0,
                    x: 20
                  }} animate={{
                    opacity: isRevealed ? 1 : 0.15,
                    x: isRevealed ? 0 : 20
                  }} transition={{
                    duration: 0.4
                  }} className="flex items-start" onMouseEnter={() => isRevealed ? setHighlightedBelief(connectedBelief) : null} onMouseLeave={() => setHighlightedBelief(null)} data-id="element-455">
                          <span className="mr-3 mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{
                      backgroundColor: color
                    }} data-id="element-456"></span>
                          <div data-id="element-457">
                            <span className="text-gray-700 leading-relaxed" data-id="element-458">
                              {priority}
                            </span>
                            {isRevealed && <motion.div initial={{
                        opacity: 0,
                        height: 0
                      }} animate={{
                        opacity: 1,
                        height: 'auto'
                      }} transition={{
                        delay: 0.2
                      }} className="flex items-center mt-1.5" data-id="element-459">
                                <Link2 size={12} className="text-gray-400 mr-1.5" data-id="element-460" />
                                <span className="text-xs text-gray-400 italic" data-id="element-461">
                                  Driven by: "
                                  {profile.beliefs[connectedBelief].slice(0, 60)}
                                  ..."
                                </span>
                              </motion.div>}
                          </div>
                        </motion.li>;
                })}
                  </ul>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>

        {/* Key Insight — only after full reveal */}
        <AnimatePresence data-id="element-462">
          {phase === 'insight' && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-463">
              <div className="bg-white border-2 rounded-3xl p-8 shadow-sm flex items-start mb-8" style={{
            borderColor: `${color}30`
          }} data-id="element-464">
                <div className="p-3 rounded-full mr-6 flex-shrink-0" style={{
              backgroundColor: `${color}15`,
              color
            }} data-id="element-465">
                  <Lightbulb size={24} data-id="element-466" />
                </div>
                <div data-id="element-467">
                  <h3 className="text-sm font-bold uppercase tracking-wider mb-1" style={{
                color
              }} data-id="element-468">
                    The Core Tension
                  </h3>
                  <p className="text-xl font-semibold text-dark" data-id="element-469">
                    {profile.tension}
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 mb-10 flex items-start" data-id="element-470">
                <MessageCircle className="text-blue-500 mr-4 mt-1 flex-shrink-0" size={24} data-id="element-471" />
                <div data-id="element-472">
                  <h4 className="text-blue-900 font-bold mb-2" data-id="element-473">
                    Now discuss with your group:
                  </h4>
                  <ul className="text-blue-800 space-y-1.5" data-id="element-474">
                    <li data-id="element-475">
                      <strong data-id="element-476">Targeting:</strong> Which {role}s are caught in
                      this tension — and most ready to shift?
                    </li>
                    <li data-id="element-477">
                      <strong data-id="element-478">Arguments:</strong> Which belief-priority
                      connection is the biggest opportunity for our messaging?
                    </li>
                    <li data-id="element-479">
                      <strong data-id="element-480">Motivation:</strong> How do you use this tension
                      to create urgency?
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex justify-center" data-id="element-481">
                <motion.button initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} onClick={nextStep} className="inline-flex items-center justify-center px-8 py-3 text-base font-semibold text-white bg-dark rounded-full hover:bg-black transition-colors" data-id="element-482">
                  Continue
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-483" />
                </motion.button>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Continue button only visible in beliefs phase if they want to skip */}
        {phase === 'beliefs' && <div className="flex justify-center mt-4" data-id="element-484">
            <p className="text-sm text-gray-400" data-id="element-485">
              Discuss your predictions, then reveal the priorities →
            </p>
          </div>}
      </div>
    </div>;
}